import { useState } from "react";

interface UseCase {
  id: string;
  title: string;
  icon: string;
  description: string;
  recommendation: "solar" | "gas" | "whole" | "solar-or-whole";
  recommendationLabel: string;
  reason: string;
  shopUrl: string;
  shopLabel: string;
}

const useCases: UseCase[] = [
  {
    id: "apartment",
    title: "Apartment / Condo Dweller",
    icon: "ri-building-line",
    description: "You rent or own in a multi-unit building. No outdoor access, no permanent installs.",
    recommendation: "solar",
    recommendationLabel: "Solar Generator",
    reason: "Solar generators are indoor-safe, silent, and require zero installation. Perfect for apartment living — charge the unit before an outage and keep critical devices running as long as you need.",
    shopUrl: "https://ironhavensupply.com/collections/solar-generators",
    shopLabel: "Shop Solar Generators",
  },
  {
    id: "suburban-family",
    title: "Suburban Family Home",
    icon: "ri-home-heart-line",
    description: "You own a single-family home. You want reliable power for your family without complexity.",
    recommendation: "solar",
    recommendationLabel: "Solar Generator (Mid–High Wattage)",
    reason: "A 2,000–3,600W solar generator covers refrigerators, lights, phones, CPAP machines, and fans. You can always expand with additional panels or battery modules as your needs grow. It's the most flexible backup system a family can own.",
    shopUrl: "https://ironhavensupply.com/collections/solar-generators",
    shopLabel: "Shop Solar Generators",
  },
  {
    id: "off-grid",
    title: "Off-Grid Living / Cabin",
    icon: "ri-tree-line",
    description: "You live in a rural area or own a cabin. Grid power isn't always available or reliable.",
    recommendation: "solar",
    recommendationLabel: "Solar Generator + Expansion Kit",
    reason: "Solar generators with expandable battery banks and multiple panel inputs are built precisely for this scenario. No gas runs, no maintenance windows — just sustainable power as long as the sun rises.",
    shopUrl: "https://ironhavensupply.com/collections/off-grid-power-systems",
    shopLabel: "Shop Off-Grid Systems",
  },
  {
    id: "medical",
    title: "Medical Equipment Dependency",
    icon: "ri-heart-pulse-line",
    description: "You or a family member relies on CPAP, oxygen concentrator, or other powered medical devices.",
    recommendation: "solar",
    recommendationLabel: "Solar Generator (Pure Sine Wave)",
    reason: "Medical devices require clean, stable pure sine wave power. Solar generators provide exactly that — plus they're silent and indoor-safe, unlike gas generators that can't be used near the people who need them most.",
    shopUrl: "https://ironhavensupply.com/collections/solar-generators",
    shopLabel: "Shop Solar Generators",
  },
  {
    id: "high-frequency-outages",
    title: "Frequent Long Outages",
    icon: "ri-flashlight-line",
    description: "Your area experiences regular outages lasting 12+ hours or multi-day events from storms.",
    recommendation: "solar-or-whole",
    recommendationLabel: "Solar Generator or Whole Home Standby",
    reason: "If budget allows, a whole-home standby generator provides automatic, seamless coverage. If you want flexibility without the installation cost, a high-capacity solar generator (3,600W+) with expandable battery banks can handle multi-day events with solar recharging.",
    shopUrl: "https://ironhavensupply.com/collections/home-backup-power",
    shopLabel: "Shop Home Backup Power",
  },
  {
    id: "job-site",
    title: "Contractor / Job Site Use",
    icon: "ri-tools-line",
    description: "You need portable power for tools, equipment, and remote sites without utility access.",
    recommendation: "gas",
    recommendationLabel: "Gas Generator (High Wattage)",
    reason: "Gas generators deliver maximum wattage-per-dollar for heavy tool loads — compressors, circular saws, welders. Solar generators work for small tools and lighting but can't match gas for sustained high-draw applications at a comparable cost.",
    shopUrl: "https://ironhavensupply.com/collections/home-backup-power",
    shopLabel: "Shop Generators",
  },
  {
    id: "luxury-seamless",
    title: "Luxury Home / Never Miss a Beat",
    icon: "ri-vip-crown-line",
    description: "Budget isn't a primary constraint. You want zero interruption, zero management, whole-home coverage.",
    recommendation: "whole",
    recommendationLabel: "Whole Home Standby Generator",
    reason: "A whole-home standby connected to natural gas activates in under 10 seconds without any human intervention. It powers everything — HVAC, appliances, security, lighting — for as long as the outage lasts. The gold standard for seamless backup.",
    shopUrl: "https://ironhavensupply.com/collections/home-backup-power",
    shopLabel: "Shop Home Backup Systems",
  },
];

const badgeColors: Record<string, string> = {
  solar: "bg-amber-500 text-white",
  gas: "bg-slate-600 text-white",
  whole: "bg-gray-700 text-white",
  "solar-or-whole": "bg-amber-500 text-white",
};

export default function UseCaseSelector() {
  const [active, setActive] = useState<string | null>(null);
  const selected = useCases.find((u) => u.id === active) ?? null;

  return (
    <section id="use-case" className="py-20 bg-white scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Find Your Match</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2 mb-3">
            Which System Is Right for You?
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Select the scenario that best matches your situation and get a personalized recommendation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Scenario selector */}
          <div className="space-y-3">
            {useCases.map((uc) => (
              <button
                key={uc.id}
                onClick={() => setActive(uc.id === active ? null : uc.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${
                  active === uc.id
                    ? "border-amber-400 bg-amber-50"
                    : "border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/30"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${active === uc.id ? "bg-amber-100" : "bg-gray-50"}`}>
                    <i className={`${uc.icon} text-lg ${active === uc.id ? "text-amber-600" : "text-gray-400"}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm ${active === uc.id ? "text-amber-700" : "text-gray-800"}`}>{uc.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-snug">{uc.description}</p>
                  </div>
                  <div className={`w-5 h-5 flex items-center justify-center shrink-0 ${active === uc.id ? "text-amber-500" : "text-gray-300"}`}>
                    <i className={`${active === uc.id ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} text-lg`}></i>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Recommendation panel */}
          <div className="sticky top-24">
            {selected ? (
              <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-900 px-6 py-5">
                  <p className="text-gray-400 text-xs uppercase tracking-widest mb-2">Recommended for you</p>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl shrink-0">
                      <i className="ri-award-line text-amber-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-extrabold text-xl leading-tight">{selected.recommendationLabel}</h3>
                      <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full ${badgeColors[selected.recommendation]}`}>
                        Best Match
                      </span>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-6">
                  <div className="flex items-start gap-3 mb-6">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl shrink-0 mt-0.5">
                      <i className={`${useCases.find(u => u.id === active)?.icon} text-amber-600 text-base`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{selected.title}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{selected.description}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-6 border-l-4 border-amber-400 pl-4 italic">
                    &ldquo;{selected.reason}&rdquo;
                  </p>
                  <a
                    href={selected.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {selected.shopLabel}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line text-sm"></i>
                    </div>
                  </a>
                  <p className="text-center text-gray-400 text-xs mt-3">
                    Still not sure?{" "}
                    <a href="tel:8666054975" className="text-amber-500 hover:underline cursor-pointer font-semibold">
                      Call us: (866) 605-4975
                    </a>
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-cursor-line text-gray-400 text-2xl"></i>
                </div>
                <p className="font-bold text-gray-700 text-base mb-2">Select a scenario</p>
                <p className="text-gray-400 text-sm">Pick the situation that matches your needs and we&apos;ll show you the best recommendation.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
